import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { sanitizeUrl, sanitizeImageProps } from '@/utils/sanitization'
import type { AttachmentGalleryProps } from './types'

import './AttachmentGallery.scss'

const getImageName = (url: string): string => {
    try {
        const pathname = new URL(url).pathname
        return decodeURIComponent(pathname.split('/').findLast(Boolean) ?? '')
    } catch {
        return url.split('/').findLast(Boolean)?.split('?')[0] ?? ''
    }
}

const AttachmentGallery = ({ items }: AttachmentGalleryProps) => {
    const { t } = useTranslation()
    const [previewIdx, setPreviewIdx] = useState<number | null>(null)

    const closePreview = useCallback(() => setPreviewIdx(null), [])

    const showPrev = useCallback(() =>
        setPreviewIdx(i => (i === null ? null : (i - 1 + items.length) % items.length)),
    [items.length])

    const showNext = useCallback(() =>
        setPreviewIdx(i => (i === null ? null : (i + 1) % items.length)),
    [items.length])

    // Arrow-key navigation (Escape is handled natively by the <dialog> cancel event)
    useEffect(() => {
        if (previewIdx !== null) {
            const onKey = (e: KeyboardEvent) => {
                if (e.key === 'ArrowLeft') showPrev()
                if (e.key === 'ArrowRight') showNext()
            }
            globalThis.addEventListener('keydown', onKey)
            return () => globalThis.removeEventListener('keydown', onKey)
        }
    }, [previewIdx, showPrev, showNext])

    const currentItem = previewIdx == null ? null : items[previewIdx]
    const currentImgFullResolution = currentItem ? sanitizeUrl(currentItem.full) : null
    const currentImgName = currentItem
        ? (currentItem.name ?? getImageName(currentItem.full))
        : ''

    if (!items.length) return null

    const imgAlt = currentImgName || `attachment ${(previewIdx ?? 0) + 1}`

    const lightbox = previewIdx !== null && (
        <dialog
            open
            className='attachment-gallery-lightbox'
            data-testid='preview-dialog'
            aria-label={`${previewIdx + 1} / ${items.length}`}
            // Escape key (native dialog cancel event)
            onCancel={e => { e.preventDefault(); closePreview() }}
            // Clicking the dark backdrop (anywhere that is not an image or arrow) closes
            onClick={e => { if (e.target === e.currentTarget) closePreview() }}
            onKeyDown={e => { if (e.key === 'Escape') closePreview() }}
        >
            <button
                type='button'
                className='a-button a-button--integrated -without-label attachment-gallery-lightbox-close'
                data-testid='dialog-close'
                aria-label='Close'
                onClick={closePreview}
            >
                <i className='a-icon a-button__icon boschicon-bosch-ic-close' />
            </button>

            {items.length > 1 && (
                <div className='attachment-gallery-lightbox-arrow-anchor attachment-gallery-lightbox-arrow-anchor--prev'>
                    <button
                        type='button'
                        className='a-button a-button--integrated -without-label attachment-gallery-lightbox-arrow'
                        data-testid='lightbox-prev'
                        aria-label={t('chat:imagePreview.prev')}
                        onClick={e => { e.stopPropagation(); showPrev() }}
                    >
                        <i className='a-icon a-button__icon boschicon-bosch-ic-back-left' />
                    </button>
                </div>
            )}

            <div className='attachment-gallery-lightbox-stage'>
                {currentImgFullResolution && (
                    <img
                        {...sanitizeImageProps({ src: currentImgFullResolution, alt: imgAlt })}
                        alt={imgAlt}
                        className='attachment-gallery-lightbox-img'
                    />
                )}
            </div>

            {items.length > 1 && (
                <div className='attachment-gallery-lightbox-arrow-anchor attachment-gallery-lightbox-arrow-anchor--next'>
                    <button
                        type='button'
                        className='a-button a-button--integrated -without-label attachment-gallery-lightbox-arrow'
                        data-testid='lightbox-next'
                        aria-label={t('chat:imagePreview.next')}
                        onClick={e => { e.stopPropagation(); showNext() }}
                    >
                        <i className='a-icon a-button__icon boschicon-bosch-ic-forward-right' />
                    </button>
                </div>
            )}

            {items.length > 1 && (
                <span
                    className='attachment-gallery-lightbox-counter'
                    role='status'
                    aria-live='polite'
                >
                    {previewIdx + 1} / {items.length}
                </span>
            )}
        </dialog>
    )

    return (
        <>
            <div className='attachment-gallery-strip' data-testid='attachment-gallery-strip'>
                {items.map((item, idx) => {
                    const thumb = sanitizeUrl(item.thumb)
                    if (!thumb) return null
                    const thumbAlt = item.name ?? `attachment ${idx + 1}`
                    return (
                        <button
                            key={`${item.thumb}-${idx}`}
                            type='button'
                            className='attachment-gallery-item'
                            aria-label={t('chat:imagePreview.viewImage', {
                                defaultValue: `View image {{n}}`,
                                n: idx + 1,
                            })}
                            onClick={() => setPreviewIdx(idx)}
                        >
                            <img
                                {...sanitizeImageProps({ src: thumb, alt: thumbAlt })}
                                alt={thumbAlt}
                                className='attachment-gallery-thumb'
                                loading='lazy'
                                decoding='async'
                            />
                        </button>
                    )
                })}
            </div>

            {lightbox && createPortal(lightbox, document.body)}
        </>
    )
}

export default AttachmentGallery
